import { AuthUtils, AuthResponse } from '../utils/auth';
import { getCsrfToken, withCsrfHeader } from '../utils/csrf';

interface User {
  user_id: string;
  username: string;
  email: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  user?: User;
  requires_2fa?: boolean;
  pending_2fa_token?: string;
}

interface RegisterResponse {
  success: boolean;
  message: string;
}

export const authService = {
  async login(username: string, password: string): Promise<LoginResponse> {
    const passwordHash = await AuthUtils.toLegacyPasswordHash(password);
    const response = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include HTTP-only cookies
      body: JSON.stringify({
        username: username,
        password: password,
        password_hash: passwordHash,
      })
    });
    
    const result = await response.json();
    if (!response.ok) {
      // FastAPI errors provide 'detail'
      const detail = typeof result?.detail === 'string' ? result.detail : undefined;
      throw new Error(result.message || detail || 'Login failed');
    }
    return result as LoginResponse;
  },
  async confirm2fa(pendingToken: string, code: string): Promise<LoginResponse> {
    const response = await fetch('/api/v1/auth/2fa/confirm', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...withCsrfHeader(),
      },
      credentials: 'include',
      body: JSON.stringify({
        pending_token: pendingToken,
        code,
      }),
    });

    const result = await response.json();
    if (!response.ok) {
      const detail = typeof result?.detail === 'string' ? result.detail : undefined;
      throw new Error(result.message || detail || '2FA confirmation failed');
    }
    return result as LoginResponse;
  },

  async register(
    username: string,
    email: string,
    phone: string,
    password: string,
    promoCode?: string,
  ): Promise<RegisterResponse> {
    const response = await fetch('/api/v1/auth/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: AuthUtils.sanitizeUsername(username),
        email: email.toLowerCase().trim(),
        phone: phone.replace(/\s/g, ''),
        promo_code: promoCode?.trim() || undefined,
        password: password
      })
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      const detail = typeof result?.detail === 'string' ? result.detail : undefined;
      throw new Error(detail || result.message || 'Registrace se nezdařila');
    }
    
    return result;
  },
  async setup2fa(): Promise<{ secret: string; qr_uri: string }> {
    const response = await fetch('/api/v1/auth/2fa/setup', {
      method: 'POST',
      headers: withCsrfHeader(),
      credentials: 'include',
    });
    const result = await response.json();
    if (!response.ok) {
      const detail = typeof result?.detail === 'string' ? result.detail : undefined;
      throw new Error(result.message || detail || '2FA setup failed');
    }
    return result;
  },
  async verify2fa(code: string): Promise<void> {
    const response = await fetch('/api/v1/auth/2fa/verify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...withCsrfHeader(),
      },
      credentials: 'include',
      body: JSON.stringify({ code }),
    });
    const result = await response.json();
    if (!response.ok) {
      const detail = typeof result?.detail === 'string' ? result.detail : undefined;
      throw new Error(result.message || detail || '2FA verification failed');
    }
  },
  async recoveryCodes(code: string): Promise<string[]> {
    const response = await fetch('/api/v1/auth/2fa/recovery', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...withCsrfHeader(),
      },
      credentials: 'include',
      body: JSON.stringify({ code }),
    });
    const result = await response.json();
    if (!response.ok) {
      const detail = typeof result?.detail === 'string' ? result.detail : undefined;
      throw new Error(result.message || detail || 'Recovery codes failed');
    }
    return result.recovery_codes || [];
  },

  async getCurrentUser(): Promise<User | null> {
    try {
      // Avoid expected 401 noise when no authenticated session is present.
      if (!getCsrfToken()) {
        return null;
      }

      const response = await fetch('/api/v1/auth/me', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        return result.user;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  },

  async logout(): Promise<void> {
    await fetch('/api/v1/auth/logout', {
      method: 'POST',
      headers: withCsrfHeader(),
      credentials: 'include'
    });
  }
};
